/**
 * 2 Nov 2026 night (D-1103-SPEED-2): one quiet "Updating…" line while a screen that is showing its last picture reads
 * again. It shows only when a read of an answer the app already holds takes more than a moment, and only in the first
 * seconds after a screen opens or the app comes back to the front — a background minute-read never shows it.
 * It is plain text: no live region, no aria-busy anywhere, nothing announced (nothing changed that needs saying).
 * Off with the Admin → Settings switch "Show the last picture while it updates".
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { keepPictureOn } from '@shared/speed-1103';
import { useDayPlan } from '@/lib/queries';

export const UPDATING_DELAY_MS = 700;
export const UPDATING_WINDOW_MS = 20_000;

export function UpdatingNote() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const location = useLocation();
  const plan = useDayPlan(false);
  const on = keepPictureOn(plan.data?.settings);
  const [show, setShow] = useState(false);
  const since = useRef(Date.now());

  // a screen opening, or the app coming back to the front, starts the window
  useEffect(() => {
    since.current = Date.now();
  }, [location.key]);
  useEffect(() => {
    const back = () => {
      if (document.visibilityState === 'visible') since.current = Date.now();
    };
    document.addEventListener('visibilitychange', back);
    window.addEventListener('pageshow', back);
    return () => {
      document.removeEventListener('visibilitychange', back);
      window.removeEventListener('pageshow', back);
    };
  }, []);

  useEffect(() => {
    if (!on) {
      setShow(false);
      return undefined;
    }
    let timer: ReturnType<typeof setTimeout> | null = null;
    const check = () => {
      const refreshing = Date.now() - since.current < UPDATING_WINDOW_MS && qc.isFetching({ predicate: (q) => q.state.data !== undefined }) > 0;
      if (!refreshing) {
        if (timer) clearTimeout(timer);
        timer = null;
        setShow(false);
      } else if (!timer) {
        timer = setTimeout(() => {
          timer = null;
          setShow(Date.now() - since.current < UPDATING_WINDOW_MS && qc.isFetching({ predicate: (q) => q.state.data !== undefined }) > 0);
        }, UPDATING_DELAY_MS);
      }
    };
    check();
    const off = qc.getQueryCache().subscribe(check);
    return () => {
      off();
      if (timer) clearTimeout(timer);
    };
  }, [on, qc, location.key]);

  if (!show) return null;
  return (
    <p className="help updating-note" data-testid="updating-note" style={{ position: 'fixed', top: 4, insetInlineEnd: 12, margin: 0, padding: '2px 10px', zIndex: 5, pointerEvents: 'none', background: 'var(--surface, #fff)', border: '1px solid var(--border, #ccc)', borderRadius: 999 }}>
      {t('updated.refreshing')}
    </p>
  );
}
